import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Components
import AdminLayout from './components/AdminLayout';
import Footer from './components/Footer';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';
import CartPopup from './components/CartPopup';
import DualRingSpinner from './components/DualRingSpinner';
import PrivacyNoticeBanner from './components/PrivacyNoticeBanner';
import AuthErrorBoundary from './components/AuthErrorBoundary';
import { AuthenticatedOnly, GuestOnly } from './components/AuthGuard';
import PageViewTracker from './components/PageViewTracker';

// Context
import { CartProvider, useCart } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProductProvider } from './context/ProductContext';

import './fonts/fonts.css';

import GuestPromoModal from './components/GuestPromoModal';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Lazy load all page components
const About = lazy(() => import('./pages/About'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const AdminCampaigns = lazy(() => import('./pages/AdminCampaigns'));
const AdminCustomerDetail = lazy(() => import('./pages/AdminCustomerDetail'));
const AdminCustomers = lazy(() => import('./pages/AdminCustomers'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminOrderDetail = lazy(() => import('./pages/AdminOrderDetail'));
const AdminOrders = lazy(() => import('./pages/AdminOrders'));
const AdminProducts = lazy(() => import('./pages/AdminProducts'));
const AdminReviews = lazy(() => import('./pages/AdminReviews'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminBlocklist = lazy(() => import('./pages/AdminBlocklist'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Contact = lazy(() => import('./pages/Contact'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const OrderHistory = lazy(() => import('./pages/OrderHistory'));
const Payment = lazy(() => import('./pages/Payment'));
const PaymentResult = lazy(() => import('./pages/PaymentResult'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Products = lazy(() => import('./pages/Products'));
const Profile = lazy(() => import('./pages/Profile'));
const Register = lazy(() => import('./pages/Register'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const MockIyzicoPayment = lazy(() => import('./pages/MockIyzicoPayment'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ReviewInvite = lazy(() => import('./pages/ReviewInvite'));
const CerezPolitikasi = lazy(() => import('./pages/docs/CerezPolitikasi'));
const GizlilikPolitikasi = lazy(() => import('./pages/docs/GizlilikPolitikasi'));
const KullanimKosullari = lazy(() => import('./pages/docs/KullanimKosullari'));
const MesafeliSatisSozlesmesi = lazy(() => import('./pages/docs/MesafeliSatisSozlesmesi'));
const TeslimatVeIadeSartlari = lazy(() => import('./pages/docs/TeslimatVeIadeSartlari'));

/* eslint-disable import/first */
const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
`;
/* eslint-enable import/first */

// Child component to use useNavigate inside Router
const GuestPromoModalWrapper = () => {
  const { isAuthenticated, loading } = useAuth();
  const [showPromo, setShowPromo] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      if (!localStorage.getItem('mgps')) {
        setShowPromo(true);
        localStorage.setItem('mgps', '1');
      }
    }
  }, [isAuthenticated, loading]);

  const handlePromoAgree = () => {
    setShowPromo(false);
    navigate('/register');
  };
  const handlePromoClose = () => {
    setShowPromo(false);
  };

  return <GuestPromoModal isOpen={showPromo} onClose={handlePromoClose} onAgree={handlePromoAgree} />;
};

// Footer is hidden on admin screens (the admin portal has its own chrome).
const ConditionalFooter = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin')) return null;
  return <Footer />;
};

const AppContent = () => {
  const { popupVisible, popupProduct, closePopup } = useCart();
  return (
    <Router>
      <PageViewTracker />
      <GuestPromoModalWrapper />
      <ScrollToTop />
      <AppContainer>
        <Navbar />
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
        <MainContent>
          <Suspense fallback={<div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'300px'}}><DualRingSpinner /></div>}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/products" element={<Products />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout/>}/>
              <Route path="/order-confirmation" element={<OrderConfirmation/>}/>
              <Route path="/payment" element={<Payment/>}/>
              <Route path="/payment-result" element={<PaymentResult/>}/>
              
              
              {/* Guest-only routes (redirect authenticated users) */}
              <Route path="/login" element={
                <GuestOnly fallback={<Navigate to="/" replace />}>
                  <Login />
                </GuestOnly>
              } />
              <Route path="/register" element={
                <GuestOnly fallback={<Navigate to="/" replace />}>
                  <Register />
                </GuestOnly>
              } />
              <Route path="/forgot-password" element={
                <GuestOnly fallback={<Navigate to="/" replace />}>
                  <ForgotPassword />
                </GuestOnly>
              } />
              <Route path="/reset-password/:token" element={
                <GuestOnly fallback={<Navigate to="/" replace />}>
                  <ResetPassword />
                </GuestOnly>
              } />
              
              {/* Protected routes (require authentication) */}
              <Route path="/order-history" element={
                <AuthenticatedOnly fallback={<Navigate to="/login" replace />}>
                  <OrderHistory />
                </AuthenticatedOnly>
              } />
              <Route path="/profile" element={
                <AuthenticatedOnly fallback={<Navigate to="/login" replace />}>
                  <Profile />
                </AuthenticatedOnly>
              } />
              
              {/* Email verification and mock payment (accessible to all) */}
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route path="/degerlendir/:token" element={<ReviewInvite />} />
              <Route path="/mock-iyzico-payment" element={<MockIyzicoPayment />} />
              
              {/* Documentation pages (public) */}
              <Route path="/docs/cerez-politikasi" element={<CerezPolitikasi />} />
              <Route path="/docs/gizlilik-politikasi" element={<GizlilikPolitikasi />} />
              <Route path="/docs/kullanim-kosullari" element={<KullanimKosullari />} />
              <Route path="/docs/mesafeli-satis-sozlesmesi" element={<MesafeliSatisSozlesmesi />} />
              <Route path="/docs/teslimat-ve-iade-sartlari" element={<TeslimatVeIadeSartlari />} />
              
              {/* Admin Routes (already have internal protection) */}
              <Route path="/admin" element={
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              } />
              <Route path="/admin/orders" element={
                <AdminLayout>
                  <AdminOrders />
                </AdminLayout>
              } />
              <Route path="/admin/orders/:id" element={
                <AdminLayout>
                  <AdminOrderDetail />
                </AdminLayout>
              } />
              <Route path="/admin/customers" element={
                <AdminLayout>
                  <AdminCustomers />
                </AdminLayout>
              } />
              <Route path="/admin/customers/:id" element={
                <AdminLayout>
                  <AdminCustomerDetail />
                </AdminLayout>
              } />
              <Route path="/admin/products" element={
                <AdminLayout>
                  <AdminProducts />
                </AdminLayout>
              } />
              <Route path="/admin/reviews" element={
                <AdminLayout>
                  <AdminReviews />
                </AdminLayout>
              } />
              <Route path="/admin/campaigns" element={
                <AdminLayout>
                  <AdminCampaigns />
                </AdminLayout>
              } />
              <Route path="/admin/analytics" element={
                <AdminLayout>
                  <AdminAnalytics />
                </AdminLayout>
              } />
              <Route path="/admin/admin-users" element={
                <AdminLayout>
                  <AdminUsers />
                </AdminLayout>
              } />
              <Route path="/admin/blocklist" element={
                <AdminLayout>
                  <AdminBlocklist />
                </AdminLayout>
              } />
            </Routes>
          </Suspense>
        </MainContent>
        <ConditionalFooter />
        <PrivacyNoticeBanner />
        <CartPopup 
          isVisible={popupVisible}
          product={popupProduct}
          onClose={closePopup}
        />
      </AppContainer>
    </Router>
  );
};

function App() {
  return (
    <AuthErrorBoundary>
      <AuthProvider>
        <ProductProvider>
          <CartProvider>
            <AppContent />
          </CartProvider>
        </ProductProvider>
      </AuthProvider>
    </AuthErrorBoundary>
  );
}

export default App;
